/*
 *
 * Copyright (c) Vaisala Oyj. All rights reserved.
 *
 */

import * as React from 'react';
import { NCInfo } from '../state/project/@types';
import { useProject } from '../state/project';
import { NCFilesCalendar } from '../components/NCFilesCalendar';
import { NCFilesInfo } from '../components/NCFilesInfo';
import { UploadStatus } from '../components/UploadStatus'
import { BodyText, Button, ButtonType, Icon, NotificationType, PageContainer, Paper, Popover, Size, Spinner, Stack } from '@vaisala/rockhopper-components';
import { useState }from 'react';
import styled from 'styled-components';

import { useNotification } from '../state/notifications';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';
import { useAuth } from '../state/auth/AuthContext';

const DataManagementContainer = styled(Paper)`

  position: relative;
  min-width: 1000px;
  width: 100%;
  height: 100%;
  overflow-y: auto;

  .find-button {
    width: 20%;
    height: 7%;
  } 
  
  .calendar-infos {
    position: relative;
    width: 100%;
    height: 70%;
  }

  .spinner-box {
    position: relative;
    width: 49%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .progress-box {
    position: relative;
    height: 23%;
    width: 100%;
    display : flex;
    flex-direction : row;
    align-items : end;
    justify-content : space-between;
    bottom: 0px;
  }

  .upload-button {
    position : relative;
    right: 0px;
    bottom: 0px;
    margin-right: 25px;
  }

`;

export type DataStatus = 'NO_DATA' | 'DATA' | 'DATA_UPLOADED';
export type PageProcessStatus = 'NONE' | 'ON_DATA_FETCHING' | 'ON_DAY_SELECT' | 'ON_UPLOAD';

const DataManagementPage = () => {  

  const [historyInput] = useState<boolean>(true);
  const {secureFetch} = useAuth()

  const {
    project, processCount,
    finderStatus, setFinderStatus,
    loadHistoryStatus,
    uploadInfo, setUploadInfo,
    ncDataHistory,
    newNCData, newNCDataUntracked
  } = useProject();

  const [histDataStatus, setHistDataStatus] = useState<DataStatus>(ncDataHistory.ok?"DATA":"NO_DATA");
  const [newDataStatus, setNewDataStatus] = useState<DataStatus>(newNCData.ok?"DATA":"NO_DATA");
  const [processStatus, setProcessStatus] = useState<PageProcessStatus>("NONE");
  const [fetchingFiles, setFetchingFiles] = useState(false)

  const [minDate, setMinDate] = useState<string>(() => {
    if (newNCData.ok && ncDataHistory.ok){
      if (new Date(newNCData.dates[0] + "T00:00:00").getTime() < new Date(ncDataHistory.dates[0] + "T00:00:00").getTime())
        return newNCData.dates[0];
      else
        return ncDataHistory.dates[0];
    } else if (newNCData.ok) {
      return newNCData.dates[0]
    } else if (ncDataHistory.ok) {
      return ncDataHistory.dates[0]
    } else {
      return ""
    }
  });
  const [maxDate, setMaxDate] = useState<string>(() => {
    if (newNCData.ok && ncDataHistory.ok){
      if (new Date(newNCData.dates[newNCData.dates.length - 1] + "T00:00:00").getTime() > new Date(ncDataHistory.dates[ncDataHistory.dates.length - 1] + "T00:00:00").getTime())
        return newNCData.dates[newNCData.dates.length - 1];
      else
        return ncDataHistory.dates[ncDataHistory.dates.length - 1];
    } else if (newNCData.ok) {
      return newNCData.dates[newNCData.dates.length - 1]
    } else if (ncDataHistory.ok) {
      return ncDataHistory.dates[ncDataHistory.dates.length - 1]
    } else {
      return ""
    }
  });

  const [ncHistoryDataDay, setNCHistoryDataDay] = useState<{[key : string] : NCInfo[]}>();
  const [ncNewDataDay, setNCNewDataDay] = useState<{[key : string] : NCInfo[]}>();
  const [ncUntrackedDataDay, setNCUntrackedDataDay] = useState<{[key : string] : NCInfo[]}>();

  const [ncDaysToUpload, setNCDaysToUpload] = useState<string[]|undefined>(newNCData.ok?newNCData.dates:undefined);
  const [checkboxValues, setCheckboxValues] = useState<boolean[]|undefined>((newNCData.ok && newNCData.nfiles_per_day)?Array(newNCData.nfiles_per_day.length).fill(true):undefined);
  const [nNewFilesDay, setNNewFilesDay] = useState<number[]|undefined>(newNCData.ok?newNCData.nfiles_per_day??undefined:undefined);
  const [currentNumberOfFiles, setCurrentNumberOfFiles] = useState<number>((newNCData.ok && newNCData.nfiles_per_day)?newNCData.nfiles_per_day.reduce((a,b)=>a+b):0);
  
  const [currentDate, setCurrentDate] = useState<string>(() => {
    if (newNCData.ok){
      return newNCData.dates[0];
    } else if (ncDataHistory.ok) {
      return ncDataHistory.dates[0]
    } else {
      return ""
    }
  });
  const [selectedMode, setSelectedMode ] = useState<"LEFT"|"RIGHT">("LEFT");

  const { notify } = useNotification();

  React.useEffect(() => {
    if (uploadInfo.status !== 'RUNNING'){
      if (finderStatus === 'RUNNING' || finderStatus === 'FINISHED' || finderStatus === 'LOADING' || loadHistoryStatus === "RUNNING")
        setProcessStatus('ON_DATA_FETCHING');
    } else {
      setProcessStatus("ON_UPLOAD");
    }
  }, [historyInput])

  React.useEffect(() => {
    if (uploadInfo.status !== 'RUNNING'){
      if (finderStatus !== 'RUNNING' && finderStatus !== 'FINISHED' && finderStatus !== 'LOADING'&& finderStatus !== 'START' && finderStatus !== 'STOP' && loadHistoryStatus !== "RUNNING" ){

        if (newNCData.ok && ncDataHistory.ok){

          setHistDataStatus("DATA");
          setNewDataStatus("DATA");

          if (new Date(newNCData.dates[0] + "T00:00:00").getTime() < new Date(ncDataHistory.dates[0] + "T00:00:00").getTime())
            setMinDate(newNCData.dates[0]);
          else
            setMinDate(ncDataHistory.dates[0]);

          if (new Date(newNCData.dates[newNCData.dates.length - 1] + "T00:00:00").getTime() > new Date(ncDataHistory.dates[ncDataHistory.dates.length - 1] + "T00:00:00").getTime())
            setMaxDate(newNCData.dates[newNCData.dates.length - 1]);
          else
            setMaxDate(ncDataHistory.dates[ncDataHistory.dates.length - 1]);

            setNCDaysToUpload(newNCData.dates);
            setCheckboxValues(newNCData.nfiles_per_day?Array(newNCData.nfiles_per_day.length).fill(true):undefined);
            setNNewFilesDay(newNCData.nfiles_per_day??undefined);
            setCurrentNumberOfFiles(newNCData.nfiles_per_day?newNCData.nfiles_per_day.reduce((a,b)=>a+b):0);

            setCurrentDate(newNCData.dates[0]);

        } else if (newNCData.ok) {

          setHistDataStatus("NO_DATA");
          setNewDataStatus("DATA");

          setMinDate(newNCData.dates[0]);
          setMaxDate(newNCData.dates[newNCData.dates.length - 1]);

          setNCDaysToUpload(newNCData.dates);
          setCheckboxValues(newNCData.nfiles_per_day?Array(newNCData.nfiles_per_day.length).fill(true):undefined);
          setNNewFilesDay(newNCData.nfiles_per_day??undefined);
          setCurrentNumberOfFiles(newNCData.nfiles_per_day?newNCData.nfiles_per_day.reduce((a,b)=>a+b):0);

          setCurrentDate(newNCData.dates[0]);

        } else if (ncDataHistory.ok) {

          setHistDataStatus("DATA");
          setNewDataStatus("NO_DATA");

          setMinDate(ncDataHistory.dates[0]);
          setMaxDate(ncDataHistory.dates[ncDataHistory.dates.length - 1]);

          setCurrentDate(ncDataHistory.dates[0]);

        } else {

          setMinDate("");
          setMaxDate("");

          setCurrentDate("");
        }

        setProcessStatus('NONE');
        setSelectedMode('LEFT');

      } else {
        setProcessStatus("ON_DATA_FETCHING");
      }
    }
  }, [loadHistoryStatus, finderStatus])

  const url = new URL(window.location.href);
  const pathSegments = url.pathname.split('/')
  const currentProjectOnScreen = pathSegments[pathSegments.indexOf('project') + 1];

  const currentProjectOnScreenIsUploading = (currentProjectOnScreen === uploadInfo.project) && uploadInfo.status ==='RUNNING'

  // Fetching new data info.
  const handleFindNewFiles = () => {
    if(currentProjectOnScreenIsUploading){
      console.log("Upload running ! ");
    } else {
      setProcessStatus('ON_DATA_FETCHING');
      setFinderStatus('START');
    }
  };

  // Fetching files infos for a specific day including file names.
  const handleClickDay = (date: Date) => {
    date.setTime(date.getTime() - date.getTimezoneOffset()*60*1000)
    let newCurrentDate = date.toISOString().split("T")[0];
    setCurrentDate(newCurrentDate);
    return;
  };

  React.useEffect(() => {
    if (currentDate && uploadInfo.status !== "RUNNING"){
      if (selectedMode === "RIGHT"){
        setProcessStatus("ON_DAY_SELECT");
      }
      handleDaySelect();
      if (finderStatus !== 'RUNNING' && finderStatus !== 'FINISHED' && finderStatus !== 'LOADING' && loadHistoryStatus !== "RUNNING" && finderStatus !== 'START' && loadHistoryStatus !== "START")
        setTimeout(() => setProcessStatus("NONE"), 500);

    }
  }, [currentDate]);

  const handleDaySelect = async () => {

    if (histDataStatus === "DATA")
      fetchHistoryDataDay();
    if (newDataStatus === "DATA")
      fetchNewDataDay();
    if (finderStatus !== 'RUNNING' && finderStatus !== 'FINISHED'  && finderStatus !== 'LOADING' && loadHistoryStatus !== "RUNNING" && finderStatus !== 'START' && loadHistoryStatus !== "START")
      setTimeout(()=> setProcessStatus('NONE'), 500);
  };

  const fetchHistoryDataDay = async () => {
    try {
      // Set fetchingFiles to true at the beginning
      setFetchingFiles(true);
  
      // First request
      const hist_response = await secureFetch(
        `/api/projects/` + project.name + '/data/' + currentDate,
        {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
  
      const history_data = await hist_response.json();
      const history_data_day: { [key: string]: NCInfo[] } = history_data.history_info;
  
      if (Object.keys(history_data_day).length === 0) {
        setNCHistoryDataDay(undefined);
      } else {
        setNCHistoryDataDay(history_data_day);
      }
  
      // Second request with `files=true` query param
      const hist_files_response = await secureFetch(
        `/api/projects/` + project.name + '/data/' + currentDate + '?files=true',
        {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
  
      const history_files_data = await hist_files_response.json();
      const history_files_data_day: { [key: string]: NCInfo[] } = history_files_data.history_info;
  
      if (Object.keys(history_files_data_day).length === 0) {
        setNCHistoryDataDay(undefined);
      } else {
        setNCHistoryDataDay(history_files_data_day);
      }
  
    } catch (error) {
      setNCHistoryDataDay(undefined);
      setProcessStatus('NONE');
      console.error(error);
    } finally {
      // Set fetchingFiles to false when the second request finishes
      setFetchingFiles(false);
    }
  };
  

  const fetchNewDataDay = async () => {

    try {
      const new_response = await secureFetch(`/api/projects/` + project.name + '/new_data/' + currentDate, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data_day = await new_response.json();

      const new_data_day : {[key : string] : NCInfo[]} = data_day.new_nc_info;
      setNCNewDataDay(new_data_day);

      if(!!!data_day.untracked){
        const untracked_data_day : {[key : string] : NCInfo[]} = data_day.untracked_nc_info;
        setNCUntrackedDataDay(untracked_data_day);
      }

    } catch (error) {

      setNCNewDataDay(undefined);
      setNCUntrackedDataDay(undefined);
      setProcessStatus('NONE');

      console.error(error);
    }
  }

  const handleToggle = (selected : string) => {
    setProcessStatus("ON_DAY_SELECT");
    if (selected === "LEFT" || selected === "RIGHT" )
      setSelectedMode(selected);
    if (finderStatus !== 'RUNNING' && finderStatus !== 'FINISHED' && finderStatus !== 'LOADING' && loadHistoryStatus !== "RUNNING" && finderStatus !== 'START' && loadHistoryStatus !== "START")
      setTimeout(() => setProcessStatus("NONE"), 500);
  }

  // Unckecking new data day checkbox
  React.useEffect(() => {

    if (newDataStatus === "DATA"){
      handleRemoveAddDay();
    }
  }, [checkboxValues]);


  const handleRemoveAddDay = () => {

    if (!newNCData.dates || !checkboxValues || !nNewFilesDay){
      return;
    }

    const newDaysToUpload : string[] = newNCData.dates.filter((_, index) => checkboxValues[index]);
    const newNFilesDays : number[] = nNewFilesDay?.filter((_, index) => checkboxValues[index]);

    const nFiles : number = newNFilesDays.length === 0 ? 0 : newNFilesDays.reduce(( a : number, b: number) => {
        return a+b;
    });

    setNCDaysToUpload(newDaysToUpload);
    setCurrentNumberOfFiles(nFiles);

    setUploadInfo({...uploadInfo, dates : newDaysToUpload, n_files : nFiles})

  }

  // Upload status update
  React.useEffect(() => {
    if (uploadInfo.status === 'START' && uploadInfo.project === project.name && newDataStatus === 'DATA'){
      fetchUpload();
    }

    else if (uploadInfo.status === 'RUNNING' && uploadInfo.project === project.name){
      setProcessStatus("ON_UPLOAD");
    } 

    else if (uploadInfo.status === 'STOP'){
      handleFinished();
    } 

    else if (uploadInfo.status === 'FINISHED'){
      handleFinished();
    } 

    else if (uploadInfo.status === 'ERROR'){
      handleFinished();
      console.log("Error during upload");
    } 

    }, [uploadInfo.status]);

  const fetchUpload = async () => {

      setUploadInfo({...uploadInfo, status: "RUNNING"})

      try {

        const response = await secureFetch(`/api/projects/` + project.name + '/new_data/upload' , {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            'dates' : ncDaysToUpload,
          })
        });

        const data = await response.json();

        if (!response.ok){
          if(response.status === 409){
            notify({
              type: NotificationType.Alarm,
              content: data.message ? data.message : "Error: An upload process is already in progress.",
              duration: 1500});
            setProcessStatus('NONE')
            setUploadInfo({...uploadInfo, project: data.project_name, status : 'INTERRUPTED'});
            return

          }
          const msg = data.message;
          console.error(msg);
          notify({
            type: NotificationType.Alarm,
            content: data.message ? data.message : "Error while uploading nc files",
            duration: 1500});
          setProcessStatus('NONE');
        } else {
          if (data.errors !== undefined && data.errors > 0){
            const errors : number = data.errors
            notify({
              type: NotificationType.Warning,
              content: errors.toString() + " files could not have been uploaded ! Check for corrupted files.",
              duration: 1500});
            setUploadInfo({...uploadInfo, status : "NONE", n_files_uploaded : currentNumberOfFiles-errors});
          }
        }

      } catch (error) {
        notify({
          type: NotificationType.Alarm,
          content: error ? String(error) : "Error while uploading nc files",
          duration: 1500});
        setProcessStatus('NONE')
        setUploadInfo({...uploadInfo, status : 'ERROR', n_files_uploaded : 0});
        console.error(error);
      }
  }
  
  const handleStopProcess = () => {
    if (uploadInfo.status === 'RUNNING'){
        setUploadInfo({...uploadInfo, status : 'STOP'});
    } else if (processStatus === "ON_DATA_FETCHING" && finderStatus === "RUNNING") {
        setFinderStatus("STOP");
    }
  }

  // Upload ending process
  const handleFinished = () => {

    setNewDataStatus('DATA_UPLOADED');
    setProcessStatus('NONE');

    setNNewFilesDay(undefined);
    setNCDaysToUpload(undefined);
    setCheckboxValues(undefined);

    setFinderStatus("NONE");

    setNCNewDataDay(undefined);
    setNCUntrackedDataDay(undefined);
    setNNewFilesDay(undefined);
    setCurrentNumberOfFiles(0);

  }

  React.useEffect(() => {
    if(finderStatus === "NOT_FOUND"){
      setProcessStatus("NONE")
    }
  }, [finderStatus])

  return (
    <PageContainer.Content>
      <DataManagementContainer>
        <div className='find-button'>
          <Button
            disabled={
              finderStatus === 'START' || 
              finderStatus === 'RUNNING' || 
              finderStatus === 'LOADING' || 
              finderStatus === 'STOP' || 
              processStatus === 'ON_DATA_FETCHING' || 
              uploadInfo.status === 'RUNNING'
            }
            onClick={ () => {
              handleFindNewFiles();
              }
            }
            buttonSize={Size.M}
          >
            Find new files
          </Button>
        </div>
        <Stack direction='row' justify={'space-between'} spacing={Size.M} className='calendar-infos'>
          {
            (processStatus === 'ON_DATA_FETCHING')?
            <Paper className='spinner-box'>
              <Spinner />
            </Paper>:
            <NCFilesCalendar
              disable={processStatus === "ON_UPLOAD" || (histDataStatus === 'NO_DATA' && newDataStatus === 'NO_DATA')}
              dates={ncDataHistory.dates}
              hist_nc_status={ncDataHistory.status}
              minDate={minDate??""}
              maxDate={maxDate??""}
              currentDate={currentDate??""}
              onClickDay={handleClickDay}
              new_dates={newNCData.dates.length > 0 ? newNCData.dates:undefined}
              new_nc_status={newNCData.status.length > 0 ? newNCData.status:undefined}
              checkbox_values={checkboxValues}
              setCheckboxValues={setCheckboxValues??undefined}
            />
          }
          {
            (processStatus === 'ON_DATA_FETCHING' || processStatus === 'ON_DAY_SELECT')?
            <Paper className='spinner-box'>
              <Spinner />
            </Paper>:
            selectedMode === "LEFT" ?
            <NCFilesInfo
              processStatus={processStatus}
              selectedMode={selectedMode}
              handleToggle={handleToggle}
              history_nc_info={ncDataHistory.data}
              history_status={ncDataHistory.status.every(v => v)}
              new_nc_info={newNCData.data}
              new_nc_status={newNCData.status.every(v => v)}
              untracked_nc_info={newNCDataUntracked.data}
              new_data = {newNCData.dates.length > 0}
              fetchingFiles = {fetchingFiles}
            /> :
            <NCFilesInfo
              processStatus={processStatus}
              selectedMode={selectedMode}
              handleToggle={handleToggle}
              date={currentDate}
              history_nc_info={ncHistoryDataDay??undefined}
              history_status={Object.values(ncHistoryDataDay??{}).map(v => v.map((ncinfo) => {return ncinfo.status})).flat().every(v => v)}
              new_nc_info={ncNewDataDay??undefined}
              new_nc_status={Object.values(ncNewDataDay??{}).map(v => v.map((ncinfo) => {return ncinfo.status})).flat().every(v => v)}
              untracked_nc_info={ncUntrackedDataDay??undefined}
              new_data = {(newNCData.dates?.findIndex(dt => (new Date(dt+"T00:00:00")).getTime() === (new Date(currentDate+"T00:00:00")).getTime())??-1) >= 0 }
              fetchingFiles = {fetchingFiles}
            />
          }
        </Stack>
        <div className='progress-box'>
          <UploadStatus
            processStatus={processStatus}
            newStatus={newDataStatus}
          />
          {
            (uploadInfo.status === 'RUNNING' || processStatus === "ON_DATA_FETCHING") ?
            <Popover
                arrow={true}
                showCloseIcon={false}
                triggerElement={
                    <Button style={{color : 'red'}} buttonType={ButtonType.Secondary} buttonSize={Size.S} 
                      disabled={
                      !((uploadInfo.status === 'RUNNING') && (uploadInfo.project === project.name)) && uploadInfo.status === 'RUNNING' && processStatus !== 'ON_DATA_FETCHING'}
                      >
                        <Icon name={VaiIcon.Stop}/>
                    </Button>
                }
            >
                <BodyText style={{marginBottom: '2px'}}>
                Are you sure?
                </BodyText>
                <Button style={{color : 'red'}} buttonType={ButtonType.Secondary}  onClick={() => handleStopProcess()}>
                    Stop
                </Button>
            </Popover> :
            <Button
              disabled={uploadInfo.status === 'STOP' || finderStatus === 'STOP' || processStatus !== 'NONE' || newDataStatus === 'NO_DATA' }
              buttonType={ButtonType.Primary}
              buttonSize={Size.M}
              onClick={() => {
                if (processCount + 2 <= 3){
                  setProcessStatus("ON_UPLOAD");
                  setUploadInfo({status : 'START', project: project.name, dates : ncDaysToUpload??[], n_files : currentNumberOfFiles, n_files_uploaded : 0});                  
                } else {
                  notify({
                    type: NotificationType.Info,
                    content: "Too many process running. Wait for a process to finish to launch this upload",
                    duration: 3000});
                }

              }}
              type="button"
              dataTa="upload-button"
              className='upload-button'>
              Upload files
            </Button>    
          }
        </div>
      </DataManagementContainer>
    </PageContainer.Content>
  );
};

export default DataManagementPage;